import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";
import { Flag, FlagOff, CheckCircle2, Circle, AlertTriangle, Clock, CalendarClock, Calendar, Save, X, Settings2, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { stageBadgeStyle, stageBarStyle } from "../lib/taskStage";

// TASKGANTT_MARKER_V3 — single timeline + planned vs actual milestones
const DAY_MS = 24 * 60 * 60 * 1000;

// TZ-safe parse for ISO YYYY-MM-DD strings. `new Date("2026-04-23")` parses as
// UTC midnight, then `.getDate()` returns the local-TZ day → in negative or
// some positive UTC offsets the round-trip drifts by -1 day. Parsing the
// components directly into a local Date side-steps the issue entirely.
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const parseDate = (s) => {
  if (!s) return null;
  if (typeof s === "string") {
    const m = ISO_DATE_RE.exec(s);
    if (m) {
      return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    }
  }
  const d = typeof s === "string" ? new Date(s) : s;
  if (!d || isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const toISODate = (d) => {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

const monthName = (m) =>
  ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m];

const fmtDay = (d) =>
  `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;

// Pretty date: "15 Feb" (no year — Gantt always shows current operational window)
const fmtPretty = (d) => `${d.getDate()} ${monthName(d.getMonth())}`;

const daysBetween = (a, b) => Math.round((b - a) / DAY_MS);

// How far along the task's own timeline it is — a visual fill under the bar
// so PM sees "how far along" without needing dates.
const stageProgressOf = (current) =>
  current?.stage_count > 1 ? Math.round((current.stage_index / (current.stage_count - 1)) * 100) : 0;

const fridaysInRange = (rangeStart, rangeEnd) => {
  const out = [];
  let cursor = new Date(rangeStart);
  while (cursor <= rangeEnd) {
    if (cursor.getDay() === 5) out.push(new Date(cursor));
    cursor = addDays(cursor, 1);
  }
  return out;
};

const WEEK_STATUS_STYLE = {
  on_time: { btn: "bg-emerald-500/30 border-emerald-400/70 text-emerald-200 hover:bg-emerald-500/40", icon: CheckCircle2, label: "On time" },
  delayed: { btn: "bg-red-500/30 border-red-400/70 text-red-200 hover:bg-red-500/40", icon: AlertTriangle, label: "Delayed" },
  at_risk: { btn: "bg-amber-500/30 border-amber-400/70 text-amber-200 hover:bg-amber-500/40", icon: AlertTriangle, label: "At risk" },
  pending: { btn: "bg-zinc-800 border-zinc-700 text-zinc-500 hover:bg-zinc-700", icon: Circle, label: "Pending" },
};

// Portal-rendered popover that escapes the Gantt's `overflow-x-auto` clipping.
// Positioned against the badge's bounding rect with viewport-edge awareness:
//   - Below the badge by default; flips above if it would overflow window bottom.
//   - Horizontally centered on badge; clamps left/right to keep inside viewport.
function WeekStatusPicker({ anchorRect, currentStatus, onPick, onClose }) {
  const ref = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: "below" });

  useEffect(() => {
    if (!anchorRect) return;
    // Measure ourselves after mount so we can flip if no space below
    const measure = () => {
      const node = ref.current;
      const w = node ? node.offsetWidth : 220;
      const h = node ? node.offsetHeight : 56;
      const margin = 8;
      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const placement = spaceBelow < h + margin ? "above" : "below";
      const top = placement === "below"
        ? Math.round(anchorRect.bottom + 6)
        : Math.round(anchorRect.top - h - 6);
      // Centered on badge horizontally; clamp to viewport
      let left = Math.round(anchorRect.left + anchorRect.width / 2 - w / 2);
      const maxLeft = window.innerWidth - w - margin;
      if (left > maxLeft) left = maxLeft;
      if (left < margin) left = margin;
      setCoords({ top, left, placement });
    };
    measure();
    // Re-measure on scroll/resize so the popover follows the anchor
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [anchorRect]);

  useEffect(() => {
    // Close on outside click / Escape
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const options = [
    { value: "pending", label: "Clear", icon: Circle, cls: "bg-zinc-800 border-zinc-700 text-zinc-400 hover:bg-zinc-700" },
    { value: "on_time", label: "On time", icon: CheckCircle2, cls: "bg-emerald-500/30 border-emerald-400/70 text-emerald-200 hover:bg-emerald-500/40" },
    { value: "at_risk", label: "At risk", icon: AlertTriangle, cls: "bg-amber-500/30 border-amber-400/70 text-amber-200 hover:bg-amber-500/40" },
    { value: "delayed", label: "Delayed", icon: AlertTriangle, cls: "bg-red-500/30 border-red-400/70 text-red-200 hover:bg-red-500/40" },
  ];

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 9999 }}
      className="flex gap-1 bg-zinc-950/95 border border-zinc-700 rounded-md p-1 shadow-xl"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {options.map((opt) => {
        const Icon = opt.icon;
        const isActive = currentStatus === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => { onPick(opt.value); onClose?.(); }}
            title={opt.label}
            className={cn(
              "inline-flex flex-col items-center justify-center gap-0.5 rounded border px-1.5 py-1 text-[9px] min-w-[44px] transition-colors",
              opt.cls,
              isActive && "ring-1 ring-white/60"
            )}
          >
            <Icon className="h-3 w-3" />
            <span className="leading-none">{opt.label}</span>
          </button>
        );
      })}
    </div>,
    document.body
  );
}

// Inline editor: the task's own timeline stages, an optional target date each.
// Renders inline (below task name) when user clicks the "Plan" icon. PM saves =
// PUT /tasks/{id}/planned-milestones which replaces the whole array.
function PlannedMilestonesEditor({ task, onSave, onCancel, busy }) {
  const initial = useMemo(() => {
    const map = {};
    (task.planned_milestones || []).forEach((m) => {
      if (m.stage_id) map[m.stage_id] = m.target_date || "";
    });
    return map;
  }, [task]);
  const [dates, setDates] = useState(initial);

  const handleSave = () => {
    const milestones = Object.entries(dates)
      .filter(([k, v]) => v && v.trim())
      .map(([stage_id, target_date]) => ({ stage_id, target_date }));
    onSave(milestones);
  };

  return (
    <div className="mt-2 p-2 rounded border border-zinc-700 bg-zinc-900/80 space-y-1.5">
      <div className="text-[10px] text-zinc-400 uppercase tracking-wide flex items-center gap-1 mb-1">
        <CalendarClock className="h-3 w-3" /> Plan target dates
      </div>
      {(task.stages || []).map((s) => (
        <div key={s.stage_id} className="flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />
          <span className="text-[10px] text-zinc-300 w-24 shrink-0">{s.name}</span>
          <Input
            type="date"
            value={dates[s.stage_id] || ""}
            onChange={(e) => setDates((p) => ({ ...p, [s.stage_id]: e.target.value }))}
            className="h-6 flex-1 text-[10px] bg-zinc-800 border-zinc-700 px-1.5"
          />
          {dates[s.stage_id] && (
            <button
              type="button"
              onClick={() => setDates((p) => ({ ...p, [s.stage_id]: "" }))}
              className="text-zinc-500 hover:text-red-400 shrink-0"
              title="Clear target"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      <div className="flex gap-1 pt-1">
        <Button type="button" size="sm" onClick={handleSave} disabled={busy} className="h-6 flex-1 text-[10px] bg-orange-500 hover:bg-orange-600">
          <Save className="h-3 w-3 mr-1" /> Save plan
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="h-6 px-2 text-[10px]">Cancel</Button>
      </div>
    </div>
  );
}

/**
 * TaskGantt — single-source-of-truth Gantt for a project.
 *
 * Two milestone layers on each bar:
 *   - Solid filled flags = actual phase transitions (from task_timeline_progress.stage_history)
 *   - Outline flags     = PM-planned target dates (from task.planned_milestones)
 *
 * Props:
 *   snapshot: array from /api/projects/{id}/task-timeline-snapshot — each item:
 *     {task_id, name, service_type, status, start_date, deadline, current_stage,
 *      stage_history[], planned_milestones[], has_real_progress}
 *   weekStatuses, onSetWeekStatus, canEditWeekStatus — Friday switchers
 *   onSavePlanned(task_id, milestones[]) — persist PM planned dates
 *   canEditPlanned — bool
 */
export default function TaskGantt({
  snapshot = [],
  weekStatuses = [],
  onSetWeekStatus,
  onSavePlanned,
  onUpdateTaskDates,
  canEditWeekStatus = false,
  canEditPlanned = false,
  emptyHint = "No tasks yet — create tasks with start_date and deadline to populate the Gantt.",
}) {
  const today = useMemo(() => parseDate(new Date().toISOString().slice(0, 10)), []);
  const [planningFor, setPlanningFor] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  // openFridayPicker: { iso, rect } | null — rect captured from the badge so the portal
  // popover can position itself relative to it without being clipped by the Gantt's
  // overflow-x-auto container.
  const [openFridayPicker, setOpenFridayPicker] = useState(null);

  // Drag state for planned flags. While dragging we update local `dragOverride`
  // (per task_id+stage_id) so the flag follows the cursor without waiting for
  // the server roundtrip. On mouseup we commit via onSavePlanned and clear.
  const [dragState, setDragState] = useState(null);
  // dragState: { taskId, stageId, originRowLeft, currentDate } | null
  const gridRowRefs = useRef({}); // task_id -> div ref (the bar-area container)

  // Drag state for the task BAR itself (left handle, right handle, or whole-bar move).
  // On commit we POST start_date/deadline updates via onUpdateTaskDates.
  const [barDrag, setBarDrag] = useState(null);
  // barDrag: { taskId, mode: 'start'|'end'|'move', initialStart: Date, initialEnd: Date, originClientX, currentStart, currentEnd } | null

  // Convert an absolute clientX to a day index inside the bar-area for a given task row.
  const clientXToDate = (taskId, clientX) => {
    const node = gridRowRefs.current[taskId];
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    const offsetX = clientX - rect.left;
    const dayIdx = Math.max(0, Math.min(totalDays - 1, Math.floor(offsetX / dayWidth)));
    return addDays(rangeStart, dayIdx);
  };

  // Bar drag effect — resize start/end or shift both
  useEffect(() => {
    if (!barDrag) return;
    const onMove = (e) => {
      const newDate = clientXToDate(barDrag.taskId, e.clientX);
      if (!newDate) return;
      setBarDrag((prev) => {
        if (!prev) return prev;
        if (prev.mode === "start") {
          // Don't let start cross past end (collapse to same day at minimum)
          const newStart = newDate > prev.initialEnd ? prev.initialEnd : newDate;
          return { ...prev, currentStart: newStart, currentEnd: prev.initialEnd };
        } else if (prev.mode === "end") {
          const newEnd = newDate < prev.initialStart ? prev.initialStart : newDate;
          return { ...prev, currentStart: prev.initialStart, currentEnd: newEnd };
        } else if (prev.mode === "move") {
          // Shift whole bar by delta days from origin
          const originDate = clientXToDate(prev.taskId, prev.originClientX);
          if (!originDate) return prev;
          const deltaDays = Math.round((newDate - originDate) / DAY_MS);
          return {
            ...prev,
            currentStart: addDays(prev.initialStart, deltaDays),
            currentEnd: addDays(prev.initialEnd, deltaDays),
          };
        }
        return prev;
      });
    };
    const onUp = async () => {
      const bd = barDrag;
      setBarDrag(null);
      if (!bd || !bd.currentStart || !bd.currentEnd) return;
      // Commit only the fields relevant to the drag mode, so we never accidentally
      // overwrite the side the user didn't touch:
      //   - "start" mode → only PUT start_date
      //   - "end" mode   → only PUT deadline
      //   - "move" mode  → PUT both (whole window shifted)
      // Also skip if nothing actually changed.
      const isoStart = toISODate(bd.currentStart);
      const isoEnd = toISODate(bd.currentEnd);
      const origStart = toISODate(bd.initialStart);
      const origEnd = toISODate(bd.initialEnd);
      let updates = null;
      if (bd.mode === "start") {
        if (isoStart === origStart) return;
        updates = { start_date: isoStart };
      } else if (bd.mode === "end") {
        if (isoEnd === origEnd) return;
        updates = { deadline: isoEnd };
      } else if (bd.mode === "move") {
        if (isoStart === origStart && isoEnd === origEnd) return;
        updates = { start_date: isoStart, deadline: isoEnd };
      }
      if (!updates) return;
      try {
        await onUpdateTaskDates?.(bd.taskId, updates);
      } catch (err) {
        // toast already raised by parent
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barDrag]);

  useEffect(() => {
    if (!dragState) return;
    const onMove = (e) => {
      const newDate = clientXToDate(dragState.taskId, e.clientX);
      if (!newDate) return;
      setDragState((prev) => prev ? { ...prev, currentDate: newDate } : prev);
    };
    const onUp = async () => {
      const ds = dragState;
      setDragState(null);
      if (!ds || !ds.currentDate) return;
      // Build the new planned_milestones array for this task: keep other stages, replace this one
      const task = snapshot.find((t) => t.task_id === ds.taskId);
      if (!task) return;
      const newPlanned = (task.planned_milestones || [])
        .filter((m) => m.stage_id !== ds.stageId)
        .map((m) => ({ stage_id: m.stage_id, target_date: m.target_date, note: m.note }));
      newPlanned.push({ stage_id: ds.stageId, target_date: toISODate(ds.currentDate) });
      try {
        await onSavePlanned?.(ds.taskId, newPlanned);
      } catch (err) {
        // toast already raised by parent
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState, snapshot]);

  // Show ALL tasks on the chart. Tasks missing both start_date and deadline render
  // as "ghost" bars defaulting to today → today+5d so PM can drag them into position.
  // Tasks missing one date render as 1-day bars at the known date (drag the empty
  // edge to extend). The `undated` array is kept for the footer hint.
  const dated = snapshot;
  const undated = snapshot.filter((t) => !parseDate(t.start_date) && !parseDate(t.deadline));

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (dated.length === 0) {
      return { rangeStart: addDays(today, -7), rangeEnd: addDays(today, 28) };
    }
    let min = null;
    let max = null;
    dated.forEach((t) => {
      const s = parseDate(t.start_date) || parseDate(t.deadline);
      const e = parseDate(t.deadline) || parseDate(t.start_date);
      if (s && (!min || s < min)) min = s;
      if (e && (!max || e > max)) max = e;
      (t.stage_history || []).forEach((h) => {
        const d = parseDate(h.entered_at);
        if (d) {
          if (!min || d < min) min = d;
          if (!max || d > max) max = d;
        }
      });
      (t.planned_milestones || []).forEach((m) => {
        const d = parseDate(m.target_date);
        if (d) {
          if (!min || d < min) min = d;
          if (!max || d > max) max = d;
        }
      });
    });
    if (today < min) min = today;
    if (today > max) max = today;
    const start = addDays(min, -3);
    const end = addDays(max, 3);
    const span = Math.round((end - start) / DAY_MS);
    if (span < 21) return { rangeStart: start, rangeEnd: addDays(start, 21) };
    return { rangeStart: start, rangeEnd: end };
  }, [dated, today]);

  const totalDays = Math.max(1, Math.round((rangeEnd - rangeStart) / DAY_MS) + 1);
  const showDays = totalDays <= 60;
  const dayWidth = showDays ? 28 : Math.max(14, Math.round(60 * 28 / totalDays));
  const gridWidth = totalDays * dayWidth;
  const labelCol = 320;
  // Layout constants for the bar-area (right side of each task row):
  //  - one lane per stage of the task's timeline, stacked at the top — BOTH planned (outline) and actual
  //    (solid) flags for each stage render on the SAME lane → PM sees plan vs
  //    fact comparison on one horizontal line per phase.
  //  - bar below the lanes (start_date → deadline block)
  const STAGE_LANE_H = 11;                         // px per lane (room for 14px flag with slight overhang)
  const STAGE_LANES_TOP = 4;
  // Rows share one height: enough lanes for the longest timeline on the chart.
  const maxLanes = Math.max(1, ...snapshot.map((t) => (t.stages || []).length));
  const STAGE_LANES_TOTAL = maxLanes * STAGE_LANE_H;
  const BAR_TOP = STAGE_LANES_TOP + STAGE_LANES_TOTAL + 4;          // 74
  const BAR_HEIGHT = 28;
  const rowHeight = BAR_TOP + BAR_HEIGHT + 4;
  // Lane of a stage = its position on the task's own timeline.
  const laneOf = (task, stageId) => Math.max(0, (task.stages || []).findIndex((st) => st.stage_id === stageId));

  const xForDate = (d) => {
    const dt = parseDate(d);
    if (!dt) return 0;
    const days = Math.round((dt - rangeStart) / DAY_MS);
    return Math.max(0, Math.min(totalDays - 1, days)) * dayWidth;
  };
  const widthForRange = (start, end) => {
    const s = parseDate(start) || parseDate(end);
    const e = parseDate(end) || parseDate(start);
    if (!s || !e) return dayWidth;
    const days = Math.max(1, Math.round((e - s) / DAY_MS) + 1);
    return days * dayWidth;
  };

  const todayLeft = (today >= rangeStart && today <= rangeEnd)
    ? Math.round((today - rangeStart) / DAY_MS) * dayWidth
    : null;

  const headerCells = [];
  if (showDays) {
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(rangeStart, i);
      headerCells.push({
        key: i,
        date: d,
        label: String(d.getDate()),
        isMonthStart: d.getDate() === 1 || i === 0,
        isFriday: d.getDay() === 5,
      });
    }
  } else {
    let cursor = new Date(rangeStart);
    while (cursor <= rangeEnd) {
      headerCells.push({
        key: cursor.toISOString(),
        date: new Date(cursor),
        label: `${monthName(cursor.getMonth())} ${cursor.getDate()}`,
        isMonthStart: cursor.getDate() <= 7,
        isFriday: false,
      });
      cursor = addDays(cursor, 7);
    }
  }

  const fridays = useMemo(() => fridaysInRange(rangeStart, rangeEnd), [rangeStart, rangeEnd]);
  const weekStatusByIso = useMemo(() => {
    const m = {};
    (weekStatuses || []).forEach((w) => { if (w?.week_ending) m[w.week_ending] = w; });
    return m;
  }, [weekStatuses]);

  const pickWeekStatus = async (iso, status) => {
    if (!canEditWeekStatus) return;
    await onSetWeekStatus?.(iso, status);
  };

  const savePlan = async (taskId, milestones) => {
    try {
      setSavingPlan(true);
      await onSavePlanned?.(taskId, milestones);
      setPlanningFor(null);
    } finally {
      setSavingPlan(false);
    }
  };

  // Project-level health stats: total tasks, in-progress, overdue, completed.
  // Renders as a sticky strip above the chart so PM gets a one-glance summary.
  const summary = useMemo(() => {
    let total = 0, inFlight = 0, overdue = 0, completed = 0, atDeadline = 0;
    snapshot.forEach((t) => {
      total += 1;
      const end = parseDate(t.deadline) || parseDate(t.start_date);
      if (t.current_stage?.is_last) {
        completed += 1;
      } else {
        inFlight += 1;
        if (end && end < today) overdue += 1;
        else if (end && daysBetween(today, end) <= 3) atDeadline += 1;
      }
    });
    return { total, inFlight, overdue, completed, atDeadline };
  }, [snapshot, today]);

  if (snapshot.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-700 bg-zinc-900/50 py-12 text-center">
        <p className="text-sm text-zinc-400">{emptyHint}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="task-gantt">
      {/* Project summary strip — one-glance health */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div className="rounded-md border border-zinc-800 bg-zinc-900/50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">Total</div>
          <div className="text-lg font-semibold text-zinc-50">{summary.total}</div>
        </div>
        <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-cyan-400">In progress</div>
          <div className="text-lg font-semibold text-cyan-200">{summary.inFlight}</div>
        </div>
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-amber-400">Due ≤ 3d</div>
          <div className="text-lg font-semibold text-amber-200">{summary.atDeadline}</div>
        </div>
        <div className={cn("rounded-md border px-3 py-2", summary.overdue > 0 ? "border-red-500/40 bg-red-500/5" : "border-zinc-800 bg-zinc-900/50")}>
          <div className={cn("text-[10px] uppercase tracking-wide", summary.overdue > 0 ? "text-red-400" : "text-zinc-500")}>Overdue</div>
          <div className={cn("text-lg font-semibold", summary.overdue > 0 ? "text-red-200" : "text-zinc-400")}>{summary.overdue}</div>
        </div>
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-emerald-400">Completed</div>
          <div className="text-lg font-semibold text-emerald-200">{summary.completed}</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-950/50">
        <div style={{ width: labelCol + gridWidth }} className="relative">
          <div className="flex sticky top-0 z-20 bg-zinc-900 border-b border-zinc-800">
            <div style={{ width: labelCol }} className="shrink-0 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 border-r border-zinc-800">
              Task · Stage · Dates · Progress
            </div>
            <div className="relative flex" style={{ width: gridWidth }}>
              {headerCells.map((cell) => (
                <div
                  key={cell.key}
                  style={{ width: dayWidth }}
                  className={cn(
                    "shrink-0 text-[10px] text-zinc-400 px-0.5 py-1 border-r border-zinc-800/50 text-center",
                    cell.isMonthStart && "text-zinc-200 font-medium",
                    cell.isFriday && "bg-zinc-800/50"
                  )}
                >
                  {cell.isMonthStart && showDays && (
                    <div className="text-[9px] text-zinc-500 leading-none">{monthName(cell.date.getMonth())}</div>
                  )}
                  <div className="leading-tight">{cell.label}</div>
                </div>
              ))}
              {/* Prominent "Today" pill anchored to the today-line in the header */}
              {todayLeft !== null && (
                <div
                  className="absolute pointer-events-none"
                  style={{ left: todayLeft + dayWidth / 2, top: 2, transform: "translateX(-50%)", zIndex: 5 }}
                >
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-500 text-white text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap">
                    Today {fmtPretty(today)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {dated.length === 0 && (
            <div className="px-4 py-6 text-xs text-zinc-500 text-center">
              No tasks with dates in this range — set <code className="text-zinc-300">start_date</code> + <code className="text-zinc-300">deadline</code> on tasks to populate the chart.
            </div>
          )}

          {/* Lane legend strip — tells PM what each colored band above each task bar means */}
          {canEditPlanned && dated.length > 0 && (
            <div className="flex border-b border-zinc-800 bg-zinc-950/70">
              <div
                style={{ width: labelCol }}
                className="shrink-0 px-3 py-1.5 border-r border-zinc-800 text-[10px] text-zinc-500"
              >
                <span className="text-zinc-400 font-medium">Stage lanes</span> — click any colored stripe above a task bar to plant a target flag
              </div>
              <div
                className="relative flex items-center px-2 gap-2 flex-wrap"
                style={{ width: gridWidth, minHeight: 24 }}
              >
                <span className="text-[10px] text-zinc-500">
                  Each task has one lane per stage of its own timeline, first stage on top — hover a lane to see its name.
                </span>
              </div>
            </div>
          )}

          {dated.map((task) => {
            const realStart = parseDate(task.start_date);
            const realEnd = parseDate(task.deadline);
            // GHOST mode: no dates at all → show as 5-day ghost bar at today so PM can
            // drag it into place. Once dragged, real dates save and ghost flips off.
            const isGhost = !realStart && !realEnd;
            const rawStart = realStart || realEnd || today;
            const rawEnd = realEnd || realStart || addDays(today, 5);
            // While dragging this task's bar, render the live (dragged) dates instead
            const isDraggingBar = barDrag && barDrag.taskId === task.task_id;
            const start = isDraggingBar ? barDrag.currentStart : rawStart;
            const end = isDraggingBar ? barDrag.currentEnd : rawEnd;
            const left = xForDate(start);
            const width = widthForRange(start, end);
            const canEditBar = !!onUpdateTaskDates;
            const current = task.current_stage || {};
            // Honor per-service-type custom colors via inline style; fall back to canonical class.
            // marker: stage-custom-color-v1
            const customBarStyle = stageBarStyle(current.color);
            const customBadgeStyle = stageBadgeStyle(current.color);
            const barCls = "bg-zinc-700/40 border-zinc-500/70 text-zinc-200";
            const history = (task.stage_history || []).filter((h) => parseDate(h.entered_at));
            const planned = (task.planned_milestones || []).filter((m) => parseDate(m.target_date));
            const badgeCls = "bg-zinc-700/30 text-zinc-300 border-zinc-600/40";
            const isPlanning = planningFor === task.task_id;

            // Compute date-readout helpers — these drive the explicit Start/End/days
            // strip that lives in the label column (so PM doesn't have to read the
            // tiny in-bar text).
            const hasStart = !!realStart;
            const hasEnd = !!realEnd;
            const totalDaysTask = daysBetween(start, end) + 1;
            const isCompleted = Boolean(current.is_last);
            const isOverdue = !isGhost && hasEnd && !isCompleted && end < today;
            const daysToDeadline = daysBetween(today, end); // negative if overdue
            const stageProgressPct = stageProgressOf(current);
            // Time-elapsed progress as a fallback
            const timeProgressPct = (() => {
              if (today <= start) return 0;
              if (today >= end) return 100;
              return Math.round((today - start) / (end - start) * 100);
            })();
            // Use stage progress if it's meaningful, else fall back to time elapsed
            const progressPct = isGhost ? 0 : (stageProgressPct > 5 ? stageProgressPct : timeProgressPct);

            // Quick-set: stuff today's date into whichever side is missing
            const quickSetDates = async (which) => {
              if (!canEditBar) return;
              const todayIso = toISODate(today);
              const updates = {};
              if (which === "start") {
                updates.start_date = todayIso;
                if (!hasEnd) updates.deadline = toISODate(addDays(today, 5));
              } else if (which === "end") {
                updates.deadline = todayIso;
                if (!hasStart) updates.start_date = todayIso;
              } else if (which === "both") {
                updates.start_date = todayIso;
                updates.deadline = toISODate(addDays(today, 5));
              }
              await onUpdateTaskDates?.(task.task_id, updates);
            };

            return (
              <div key={task.task_id} className="flex border-b border-zinc-800/60 hover:bg-zinc-900/40">
                <div style={{ width: labelCol }} className="shrink-0 px-3 py-2 border-r border-zinc-800 min-w-0">
                  <div className="flex items-start justify-between gap-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-zinc-50 truncate" title={task.name}>{task.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span
                          className={cn(
                            "inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium",
                            !customBadgeStyle && badgeCls,
                          )}
                          style={customBadgeStyle || undefined}
                        >
                          {current.name || "—"}
                        </span>
                        {task.service_type && (
                          <span className="text-[10px] text-zinc-500 truncate">{task.service_type}</span>
                        )}
                      </div>
                    </div>
                    {canEditPlanned && !isPlanning && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setPlanningFor(task.task_id)}
                        className="h-6 px-1.5 text-[10px] text-zinc-400 hover:text-fuchsia-300 hover:bg-fuchsia-500/10 shrink-0"
                        title="Open bulk date picker (or just click/drag flags directly on the chart →)"
                      >
                        <CalendarClock className="h-3 w-3 mr-0.5" /> Picker
                      </Button>
                    )}
                  </div>

                  {/* Date readout strip: Start → Deadline with day count + quick-set buttons */}
                  <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px]">
                    <div className="flex items-center gap-1 text-zinc-400 min-w-0">
                      <Calendar className="h-3 w-3 text-zinc-500 shrink-0" />
                      {hasStart ? (
                        <span className="font-mono">{fmtPretty(realStart)}</span>
                      ) : canEditBar ? (
                        <button
                          type="button"
                          onClick={() => quickSetDates("start")}
                          className="font-mono text-fuchsia-300 hover:text-fuchsia-200 hover:bg-fuchsia-500/10 rounded px-1 italic"
                          title="Click to set start date = today"
                        >
                          set start
                        </button>
                      ) : (
                        <span className="font-mono text-zinc-600 italic">no start</span>
                      )}
                      <span className="text-zinc-600">→</span>
                      {hasEnd ? (
                        <span className="font-mono text-zinc-300">{fmtPretty(realEnd)}</span>
                      ) : canEditBar ? (
                        <button
                          type="button"
                          onClick={() => quickSetDates("end")}
                          className="font-mono text-fuchsia-300 hover:text-fuchsia-200 hover:bg-fuchsia-500/10 rounded px-1 italic"
                          title="Click to set deadline = today"
                        >
                          set deadline
                        </button>
                      ) : (
                        <span className="font-mono text-zinc-600 italic">no deadline</span>
                      )}
                      {!isGhost && (
                        <>
                          <span className="text-zinc-600">·</span>
                          <span className="text-zinc-500">{totalDaysTask}d</span>
                        </>
                      )}
                    </div>
                    {isGhost ? (
                      canEditBar && (
                        <button
                          type="button"
                          onClick={() => quickSetDates("both")}
                          className="text-fuchsia-300 hover:text-fuchsia-200 hover:bg-fuchsia-500/10 rounded px-1.5 py-0.5 font-medium shrink-0 border border-fuchsia-500/30"
                          title="Set start=today, deadline=today+5d"
                        >
                          schedule now
                        </button>
                      )
                    ) : isCompleted ? (
                      <span className="text-emerald-400 font-medium shrink-0">✓ Done</span>
                    ) : isOverdue ? (
                      <span className="text-red-400 font-medium shrink-0">
                        {Math.abs(daysToDeadline)}d overdue
                      </span>
                    ) : hasEnd && daysToDeadline >= 0 ? (
                      <span className={cn("font-medium shrink-0", daysToDeadline <= 3 ? "text-amber-400" : "text-zinc-400")}>
                        {daysToDeadline === 0 ? "due today" : `${daysToDeadline}d left`}
                      </span>
                    ) : null}
                  </div>

                  {/* Progress bar — visual "how far along" cue. Color = stage color. */}
                  <div className="mt-1.5 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${progressPct}%`,
                        background: current.color || "#6366f1",
                      }}
                    />
                  </div>
                  <div className="mt-0.5 flex items-center justify-between text-[9px] text-zinc-500">
                    <span>{progressPct}% · {current.name || "—"}</span>
                    {hasStart && !isCompleted && (
                      <span>
                        Day {Math.max(1, Math.min(totalDaysTask, daysBetween(start, today) + 1))} of {totalDaysTask}
                      </span>
                    )}
                  </div>

                  {planned.length > 0 && !isPlanning && (
                    <div className="mt-1 text-[9px] text-zinc-500">
                      {planned.length} target{planned.length > 1 ? "s" : ""} planned
                    </div>
                  )}
                  {isPlanning && (
                    <PlannedMilestonesEditor
                      task={task}
                      busy={savingPlan}
                      onSave={(milestones) => savePlan(task.task_id, milestones)}
                      onCancel={() => setPlanningFor(null)}
                    />
                  )}
                </div>

                <div
                  className="relative"
                  style={{ width: gridWidth, height: rowHeight }}
                  ref={(el) => { gridRowRefs.current[task.task_id] = el; }}
                >
                  {/* Friday bands */}
                  {showDays && headerCells.filter((c) => c.isFriday).map((c) => {
                    const dayIdx = Math.round((c.date - rangeStart) / DAY_MS);
                    return (
                      <div
                        key={`fri-${c.key}`}
                        className="absolute top-0 bottom-0 pointer-events-none"
                        style={{ left: dayIdx * dayWidth, width: dayWidth, background: "rgba(255,255,255,0.025)" }}
                      />
                    );
                  })}

                  {/* Today marker */}
                  {todayLeft !== null && (
                    <div
                      className="absolute top-0 bottom-0 w-px bg-orange-500/70 pointer-events-none z-10"
                      style={{ left: todayLeft + dayWidth / 2 }}
                    />
                  )}

                  {/* The task's stage lanes — ALWAYS visible thin colored bands stacked
                      above the bar. Click anywhere on a lane to set that stage's target_date.
                      Hover highlights the lane (clearer affordance). */}
                  {canEditPlanned && (task.stages || []).map((s, sIdx) => {
                    const laneTop = STAGE_LANES_TOP + sIdx * STAGE_LANE_H;
                    return (
                      <div
                        key={`track-${s.stage_id}`}
                        className="absolute cursor-crosshair group"
                        style={{ left: 0, top: laneTop, width: gridWidth, height: STAGE_LANE_H - 1 }}
                        title={`${s.name} — click any day to set target (drag a flag to move, double-click to clear)`}
                        onClick={async (e) => {
                          if (dragState) return;
                          const date = clientXToDate(task.task_id, e.clientX);
                          if (!date) return;
                          const iso = toISODate(date);
                          const newPlanned = (task.planned_milestones || [])
                            .filter((m) => m.stage_id !== s.stage_id)
                            .map((m) => ({ stage_id: m.stage_id, target_date: m.target_date, note: m.note }));
                          newPlanned.push({ stage_id: s.stage_id, target_date: iso });
                          await onSavePlanned?.(task.task_id, newPlanned);
                        }}
                      >
                        <div
                          className="h-full rounded-sm transition-all"
                          style={{
                            background: s.color,
                            opacity: 0.12,
                          }}
                        />
                        <div
                          className="h-full absolute inset-0 rounded-sm transition-all opacity-0 group-hover:opacity-50"
                          style={{ background: s.color }}
                        />
                      </div>
                    );
                  })}

                  {/* Planned milestone flags (OUTLINE) — each sits on its stage lane,
                      draggable horizontally. Pole anchored at column center. */}
                  {planned.map((m) => {
                    const isDragging = dragState && dragState.taskId === task.task_id && dragState.stageId === m.stage_id;
                    const liveDate = isDragging ? dragState.currentDate : parseDate(m.target_date);
                    if (!liveDate || liveDate < rangeStart || liveDate > rangeEnd) return null;
                    const dayIdx = Math.round((liveDate - rangeStart) / DAY_MS);
                    const POLE_OFFSET = 2;
                    const mLeft = dayIdx * dayWidth + Math.floor(dayWidth / 2) - POLE_OFFSET;
                    const laneIdx = laneOf(task, m.stage_id);
                    // Flag is 14px tall, lane is 11px → small overhang both sides (acceptable)
                    const flagTop = STAGE_LANES_TOP + laneIdx * STAGE_LANE_H - 2;
                    return (
                      <div
                        key={`plan-${m.stage_id}`}
                        className={cn(
                          "absolute z-30 select-none",
                          canEditPlanned ? "cursor-grab active:cursor-grabbing" : "cursor-default",
                          isDragging && "scale-125"
                        )}
                        style={{ left: mLeft, top: flagTop, color: m.color || "#a78bfa" }}
                        title={
                          canEditPlanned
                            ? `Planned: ${m.name} by ${isDragging ? toISODate(liveDate) : m.target_date} — drag to change, double-click to clear`
                            : `Planned: ${m.name} by ${m.target_date}`
                        }
                        onMouseDown={(e) => {
                          if (!canEditPlanned) return;
                          e.preventDefault();
                          e.stopPropagation();
                          setDragState({
                            taskId: task.task_id,
                            stageId: m.stage_id,
                            currentDate: liveDate,
                          });
                        }}
                        onDoubleClick={async (e) => {
                          if (!canEditPlanned) return;
                          e.stopPropagation();
                          const newPlanned = (task.planned_milestones || [])
                            .filter((x) => x.stage_id !== m.stage_id)
                            .map((x) => ({ stage_id: x.stage_id, target_date: x.target_date, note: x.note }));
                          await onSavePlanned?.(task.task_id, newPlanned);
                        }}
                      >
                        <Flag className="h-3.5 w-3.5 drop-shadow" strokeWidth={2.5} fill="rgba(0,0,0,0.45)" />
                      </div>
                    );
                  })}

                  {/* Task bar — draggable. Three zones: left handle (resize start),
                      body (shift both dates), right handle (resize deadline).
                      For narrow bars handles are rendered OUTSIDE the bar so they're
                      always grabable, with clear chevron icons indicating direction. */}
                  {(() => {
                    // Slim handles on edges. For narrow bars handles overflow slightly
                    // outward so they're still grabable; for wide bars they sit on the
                    // bar's edge.
                    const handleW = 8;
                    const isNarrow = width < 50;
                    const leftHandleX = isNarrow ? left - handleW + 2 : left - 1;
                    const rightHandleX = isNarrow ? left + width - 2 : left + width - handleW + 1;
                    const startDrag = (mode, e) => {
                      if (!canEditBar) return;
                      e.preventDefault();
                      e.stopPropagation();
                      setBarDrag({
                        taskId: task.task_id,
                        mode,
                        initialStart: rawStart,
                        initialEnd: rawEnd,
                        originClientX: e.clientX,
                        currentStart: rawStart,
                        currentEnd: rawEnd,
                      });
                    };
                    return (
                      <>
                        {/* Bar body — ghost (dashed, semi-transparent) when no real dates */}
                        <div
                          className={cn(
                            "absolute rounded overflow-hidden whitespace-nowrap",
                            !customBarStyle && barCls,
                            isGhost ? "border-2 border-dashed opacity-50" : "border-2",
                            isDraggingBar && "ring-2 ring-white/40 shadow-lg opacity-100"
                          )}
                          style={{ left, top: BAR_TOP, height: BAR_HEIGHT, width, ...(customBarStyle || {}) }}
                          title={
                            isGhost
                              ? `${task.name}: no dates set yet — drag this ghost bar to place it on the timeline`
                              : `${task.name}: ${start ? fmtPretty(start) : "—"} → ${end ? fmtPretty(end) : "—"}${canEditBar ? " — drag chevrons on edges to resize, body to shift whole window" : ""}`
                          }
                          onMouseDown={(e) => startDrag("move", e)}
                        >
                          <div
                            className={cn(
                              "absolute inset-0 flex items-center overflow-hidden whitespace-nowrap pointer-events-none",
                              width >= 80 ? "px-2 justify-between gap-2 text-[11px] font-semibold" : "px-1 justify-center text-[10px]"
                            )}
                          >
                            {isGhost ? (
                              <span className="w-full text-center text-[10px] italic opacity-90">drag to schedule</span>
                            ) : width >= 160 ? (
                              <>
                                <span className="shrink-0">{start ? fmtPretty(start) : "—"}</span>
                                <span className="text-[9px] font-normal opacity-80 truncate">{current.name || ""}</span>
                                <span className="shrink-0">{end ? fmtPretty(end) : "—"}</span>
                              </>
                            ) : width >= 80 ? (
                              <>
                                <span className="shrink-0">{start ? fmtPretty(start) : ""}</span>
                                <span className="shrink-0">{end ? fmtPretty(end) : ""}</span>
                              </>
                            ) : null}
                          </div>
                          {canEditBar && (
                            <div className="absolute inset-0 cursor-grab active:cursor-grabbing" aria-hidden />
                          )}
                        </div>

                        {/* Left resize handle — slim white strip */}
                        {canEditBar && (
                          <div
                            className="absolute z-20 cursor-ew-resize bg-white/50 hover:bg-white/80 rounded-l transition-colors"
                            style={{
                              left: leftHandleX,
                              top: BAR_TOP,
                              height: BAR_HEIGHT,
                              width: handleW,
                            }}
                            title={`Drag ← to change start date (current: ${start ? fmtPretty(start) : "—"})`}
                            onMouseDown={(e) => startDrag("start", e)}
                          />
                        )}

                        {/* Right resize handle — slim white strip */}
                        {canEditBar && (
                          <div
                            className="absolute z-20 cursor-ew-resize bg-white/50 hover:bg-white/80 rounded-r transition-colors"
                            style={{
                              left: rightHandleX,
                              top: BAR_TOP,
                              height: BAR_HEIGHT,
                              width: handleW,
                            }}
                            title={`Drag → to change deadline (current: ${end ? fmtPretty(end) : "—"})`}
                            onMouseDown={(e) => startDrag("end", e)}
                          />
                        )}
                      </>
                    );
                  })()}

                  {/* Actual stage-history flags (SOLID) — render on the SAME lane
                      as planned for the matching stage. PM sees outline (planned)
                      and solid (actual) side-by-side on one row per stage → delivery
                      slip is visually obvious. */}
                  {history.map((h, idx) => {
                    const hd = parseDate(h.entered_at);
                    if (!hd || hd < rangeStart || hd > rangeEnd) return null;
                    const hDayIdx = Math.round((hd - rangeStart) / DAY_MS);
                    const hLeft = hDayIdx * dayWidth + Math.floor(dayWidth / 2) - 2;
                    const isCurrent = idx === history.length - 1;
                    const histLaneIdx = laneOf(task, h.stage_id);
                    const histTop = STAGE_LANES_TOP + histLaneIdx * STAGE_LANE_H - 2;
                    return (
                      <div
                        key={`act-${h.stage_id}-${idx}`}
                        className="absolute z-20"
                        style={{ left: hLeft, top: histTop, color: h.color || "#a78bfa" }}
                        title={`Actual: ${h.name} — entered ${h.entered_at?.slice(0, 10)}${h.updated_by_name ? ` by ${h.updated_by_name}` : ""}${isCurrent ? " (current)" : ""}`}
                      >
                        <Flag className="h-3.5 w-3.5" fill="currentColor" strokeWidth={isCurrent ? 2 : 1.5} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Friday switchers — click opens portal-rendered 3-state picker */}
          {fridays.length > 0 && (
            <div className="flex border-b border-zinc-800 bg-zinc-900/50">
              <div style={{ width: labelCol }} className="shrink-0 px-3 py-2 border-r border-zinc-800 text-[10px] uppercase tracking-wide text-zinc-400 flex items-center gap-1.5">
                <Clock className="h-3 w-3 text-zinc-500" />
                Weekly check (Fri)
              </div>
              <div className="relative" style={{ width: gridWidth, height: 40 }}>
                {fridays.map((friday) => {
                  const iso = toISODate(friday);
                  const dayIdx = Math.round((friday - rangeStart) / DAY_MS);
                  const status = weekStatusByIso[iso]?.status || "pending";
                  const style = WEEK_STATUS_STYLE[status] || WEEK_STATUS_STYLE.pending;
                  const Icon = style.icon;
                  const leftPx = dayIdx * dayWidth + (showDays ? dayWidth / 2 - 11 : 0);
                  return (
                    <button
                      key={iso}
                      type="button"
                      disabled={!canEditWeekStatus}
                      onClick={(e) => {
                        if (!canEditWeekStatus) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        // Toggle: if same badge already open, close
                        if (openFridayPicker?.iso === iso) {
                          setOpenFridayPicker(null);
                        } else {
                          setOpenFridayPicker({ iso, rect });
                        }
                      }}
                      title={`Fri ${iso} — ${style.label}${canEditWeekStatus ? " (click to change)" : ""}`}
                      className={cn(
                        "absolute top-2 inline-flex items-center justify-center rounded-md border h-6 w-6 transition-colors",
                        style.btn,
                        !canEditWeekStatus && "cursor-default"
                      )}
                      style={{ left: leftPx }}
                    >
                      <Icon className="h-3 w-3" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Portal-rendered picker (outside the overflow-x-auto container) */}
          {openFridayPicker && canEditWeekStatus && (
            <WeekStatusPicker
              anchorRect={openFridayPicker.rect}
              currentStatus={weekStatusByIso[openFridayPicker.iso]?.status || "pending"}
              onPick={(v) => pickWeekStatus(openFridayPicker.iso, v)}
              onClose={() => setOpenFridayPicker(null)}
            />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 text-[10px] text-zinc-500 px-1">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Flag className="h-3 w-3" fill="none" strokeWidth={2} /> Planned target</span>
          <span className="flex items-center gap-1"><Flag className="h-3 w-3" fill="currentColor" /> Actual phase entry</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-violet-400" /> Assigned</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-cyan-400" /> In Progress</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400" /> Stuck</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-400" /> AD Review</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-fuchsia-400" /> Client's Revision</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400" /> Completed</span>
          <span className="flex items-center gap-1"><span className="w-px h-3 bg-orange-500/70" /> Today</span>
        </div>
        {undated.length > 0 && (
          <span className="text-amber-400">
            {undated.length} task{undated.length > 1 ? "s" : ""} without dates
          </span>
        )}
      </div>

      <p className="text-[10px] text-zinc-500 px-1 leading-relaxed">
        <strong className="text-zinc-300">Stage lanes (above bar):</strong> each colored stripe = one stage of the task's timeline. Outline flag = PM <em>planned</em> target; solid flag = <em>actual</em> phase entry. Gap between them = delivery slip on that phase. Click a stripe to plant a target, drag to move, double-click to clear.
        {" "}<strong className="text-zinc-300">Task bar:</strong> drag edges to resize start/deadline, drag body to shift.
        Stages/colors from <a href="/settings/timelines" className="text-orange-400 hover:underline">/settings/timelines</a>.
        {canEditWeekStatus && fridays.length > 0 && " Click a Friday badge to pick weekly status."}
      </p>
    </div>
  );
}
